import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getClientDossier } from '../services/clientService';
import { urlPdfDevis } from '../services/devisService';
import { urlPdfContrat } from '../services/contratService';
import ClientDrive from './ClientDrive';
import { getDocumentsGeneres } from '../services/documentClientService';
import ClientFormulaire from './ClientFormulaire';
import EmailsEnvoyes from './EmailsEnvoyes';
import {
  STATUT_LABELS,
  STATUT_DEVIS_LABELS,
  formatDate,
  formatDateTime,
  formatMontant, formaterTelephone } from './devisUtils';
import '../assets/styles/TexteBorne.css';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Devis.css';
import '../assets/styles/Clients.css';

const LIBELLE_SOURCE = {
  brevo: 'Importé de Brevo',
  demande: 'Créé par une demande de devis',
  manuel: 'Saisi dans le back-office',
};

const ouiNon = (v) => (v === null || v === undefined ? '—' : v ? 'Oui' : 'Non');

/** Champs de la fiche, dans l'ordre de l'export Brevo. */
function champsFiche(client) {
  return [
    ['Email', client.email],
    ['Sexe', client.sexe],
    ['Fonction', client.fonction],
    ["Secteur d'activité", client.secteurActivite],
    ['Téléphone fixe', formaterTelephone(client.telephoneFixe)],
    ['WhatsApp', formaterTelephone(client.whatsapp)],
    ['Abonné', ouiNon(client.abonne)],
    ['Bloqué', ouiNon(client.bloque)],
    ['Utilisateur assigné', client.utilisateurAssigne],
    ['Contact', client.contact],
    ['Identifiant Brevo', client.identifiantBrevo],
    ['Créé dans Brevo', client.brevoCreeLe ? formatDateTime(client.brevoCreeLe) : null],
    ['Modifié dans Brevo', client.brevoModifieLe ? formatDateTime(client.brevoModifieLe) : null],
    ['Origine', LIBELLE_SOURCE[client.source] || client.source],
  ];
}

/**
 * Clients > dossier d'un client : sa fiche, ses demandes (devis, contrats) et
 * ses pièces — le « drive » du client.
 *
 * Trois onglets plutôt qu'une seule colonne : le dossier d'un client de
 * longue date empilait fiche, demandes et pièces sur plusieurs écrans de
 * défilement, et ce qu'on venait chercher — souvent un fichier — arrivait
 * tout en bas.
 */
function ClientDossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edition, setEdition] = useState(false);
  const [onglet, setOnglet] = useState('fiche');
  // Les documents générés pour ce client : ils comptent dans l'onglet et
  // s'affichent dans le drive, à côté des pièces déposées.
  const [generes, setGeneres] = useState([]);
  // Recherche dans l'onglet Demandes.
  const [rechercheDemandes, setRechercheDemandes] = useState('');

  const charger = useCallback(() => {
    return Promise.all([
      getClientDossier(id)
        .then((res) => setDossier(res.data))
        .catch((err) => setError(err.message || 'Client introuvable.')),
      getDocumentsGeneres(id)
        .then((res) => setGeneres(res.data || []))
        .catch(() => setGeneres([])),
    ]);
  }, [id]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    charger().finally(() => setLoading(false));
  }, [charger]);

  /**
   * Les pièces du client, à plat : chaque pièce attendue avec son dépôt s'il
   * existe, puis les pièces libres. C'est la matière de l'onglet Documents.
   */
  const pieces = useMemo(() => {
    if (!dossier) return { attendues: [], libres: [], recues: 0, manquantes: 0 };

    // Une même pièce réclamée par deux devis de la même demande reste une
    // seule pièce à fournir : on garde l'occurrence qui porte le dépôt.
    const parPiece = new Map();
    const idsDeposes = new Set();

    for (const dem of dossier.demandes) {
      for (const d of dem.devis) {
        for (const a of d.documents?.attendus || []) {
          const cle = `${dem.id}-${a.id}`;
          if (!parPiece.has(cle) || (a.depot && !parPiece.get(cle).depot)) {
            parPiece.set(cle, { ...a, cle, demande: dem, devisId: d.id, devisNumero: d.numero });
          }
          if (a.depot) idsDeposes.add(a.depot.id);
        }
      }
    }
    const attendues = [...parPiece.values()];

    const libres = dossier.demandes.flatMap((dem) =>
      dem.documents.filter((doc) => !idsDeposes.has(doc.id)).map((doc) => ({ ...doc, demande: dem }))
    );

    return {
      attendues,
      libres,
      recues: attendues.filter((a) => a.depot).length + libres.length,
      manquantes: attendues.filter((a) => a.obligatoire && !a.depot).length,
    };
  }, [dossier]);

  const nomComplet = dossier ? [dossier.prenom, dossier.nom].filter(Boolean).join(' ') : '';

  const ONGLETS = dossier
    ? [
        { cle: 'fiche', label: 'Fiche' },
        { cle: 'demandes', label: 'Demandes', compte: dossier.demandes.length },
        {
          cle: 'documents',
          label: 'Documents',
          // Déposées ET générées : c'est tout ce que l'onglet montre.
          compte: pieces.recues + generes.length,
          alerte: pieces.manquantes > 0,
        },
        { cle: 'emails', label: 'Emails' },
      ]
    : [];

  return (
    <div className="devis-page devis-page-scroll">
      <div className="devis-header">
        <div>
          <div className="clients-dossier-tete">
            <button type="button" className="clients-retour" onClick={() => navigate('/clients')}>
              ← Clients
            </button>
          </div>
          <h1 className="devis-title">{nomComplet || dossier?.email || 'Client'}</h1>
          <p className="devis-subtitle">
            {dossier
              ? [dossier.email, dossier.fonction, dossier.secteurActivite]
                  .filter(Boolean)
                  .join(' · ')
              : 'Dossier client'}
          </p>
        </div>
        {dossier && (
          <div className="devis-header-actions">
            <button type="button" className="devis-btn" onClick={() => setEdition(true)}>
              Modifier la fiche
            </button>
          </div>
        )}
      </div>

      {loading && <div className="devis-state">Chargement du dossier...</div>}
      {!loading && error && <div className="devis-state devis-state-error">{error}</div>}

      {!loading && dossier && (
        <>
          <nav className="clients-onglets">
            {ONGLETS.map((o) => (
              <button
                key={o.cle}
                type="button"
                className={'clients-onglet' + (onglet === o.cle ? ' clients-onglet-actif' : '')}
                onClick={() => setOnglet(o.cle)}
              >
                {o.label}
                {o.compte !== undefined && (
                  <span
                    className={
                      'clients-onglet-compte' +
                      (o.alerte ? ' clients-onglet-compte-alerte' : '')
                    }
                  >
                    {o.compte}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* ---------------- Fiche ---------------- */}
          {onglet === 'fiche' && (
            <div className="devis-card clients-demande">
              <div className="clients-fiche">
                {champsFiche(dossier).map(([libelle, valeur]) => (
                  <div className="clients-fiche-champ" key={libelle}>
                    <div className="clients-fiche-libelle">{libelle}</div>
                    <div className="clients-fiche-valeur">{valeur || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ---------------- Demandes ---------------- */}
          {onglet === 'demandes' && dossier.demandes.length === 0 && (
            <div className="devis-card clients-demande">
              <div className="devis-empty">
                <div className="devis-empty-title">Aucune demande de devis pour ce client.</div>
                <div className="devis-empty-subtitle">
                  {dossier.email
                    ? `Les demandes reçues avec l’adresse ${dossier.email} apparaîtront ici.`
                    : 'Cette fiche n’a pas d’email : aucune demande ne peut s’y rattacher automatiquement.'}
                </div>
              </div>
            </div>
          )}

          {onglet === 'demandes' && dossier.demandes.length > 0 && (
            <>
            <div className="devis-toolbar" style={{ marginTop: 12 }}>
              <input
                type="search"
                className="devis-search"
                placeholder="Rechercher une demande (objet, statut, devis, contrat...)"
                value={rechercheDemandes}
                onChange={(e) => setRechercheDemandes(e.target.value)}
              />
            </div>
            <div className="clients-demandes-grille">
              {dossier.demandes
                .filter((dem) => {
                  const mots = rechercheDemandes
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase()
                    .split(/\s+/)
                    .filter(Boolean);
                  if (!mots.length) return true;
                  const texte = [
                    dem.objet_message,
                    dem.message,
                    dem.dernier_statut,
                    STATUT_LABELS[dem.dernier_statut],
                    dem.societe_nom,
                    dem.ville,
                    ...(dem.devis || []).map((d) => d.numero),
                    ...(dem.contrats || []).map((c) => c.numero),
                  ]
                    .filter(Boolean)
                    .join(' ')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase();
                  return mots.every((m) => texte.includes(m));
                })
                .map((dem) => (
                <article
                  className="clients-demande-carte"
                  key={dem.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/demande-de-devis?contact=${dem.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/demande-de-devis?contact=${dem.id}`);
                    }
                  }}
                >
                  <div className="clients-demande-carte-tete">
                    <span className="clients-demande-carte-date">
                      {formatDate(dem.date_soumission)}
                    </span>
                    <span className="devis-status-pill">
                      {STATUT_LABELS[dem.dernier_statut] || dem.dernier_statut}
                    </span>
                  </div>
                  <h3 className="clients-demande-carte-objet">
                    {dem.objet_message || 'Demande de devis'}
                  </h3>
                  <div className="clients-demande-carte-meta">
                    {[
                      dem.societe_nom,
                      dem.effectif_societe ? `${dem.effectif_societe} salariés` : null,
                      dem.ville,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  {dem.message && (
                    <p className="clients-demande-carte-message texte-borne">{dem.message}</p>
                  )}

                  {/* Les documents produits : un bouton chacun, sans ouvrir la fiche. */}
                  <div className="clients-demande-carte-pied" onClick={(e) => e.stopPropagation()}>
                    {dem.devis.length === 0 && dem.contrats.length === 0 && (
                      <span className="clients-vide">Aucun devis généré.</span>
                    )}
                    {dem.devis.map((d) => (
                      <button
                        type="button"
                        className="devis-btn devis-btn-sm clients-demande-carte-doc"
                        key={`d${d.id}`}
                        disabled={!d.valide_le}
                        title={
                          d.valide_le
                            ? `Voir le devis ${d.numero}`
                            : `Devis ${d.numero} : pas encore validé`
                        }
                        onClick={() => window.open(urlPdfDevis(d.id, { apercu: true }), '_blank')}
                      >
                        Devis {d.numero}
                        <span>
                          {formatMontant(d.montant_ttc)} TTC ·{' '}
                          {d.signature_le ? 'signé' : STATUT_DEVIS_LABELS[d.statut] || d.statut}
                        </span>
                      </button>
                    ))}
                    {dem.contrats.map((ct) => (
                      <button
                        type="button"
                        className="devis-btn devis-btn-sm clients-demande-carte-doc"
                        key={`c${ct.id}`}
                        title={`Voir le contrat ${ct.numero}`}
                        onClick={() => window.open(urlPdfContrat(ct.id, { apercu: true }), '_blank')}
                      >
                        Contrat {ct.numero}
                        <span>
                          {formatMontant(ct.montant_ttc)} TTC ·{' '}
                          {ct.signature_client_le ? 'signé par le client' : 'non signé'}
                        </span>
                      </button>
                    ))}
                  </div>
                </article>
              ))}
            </div>
            </>
          )}

          {/* ---------------- Documents ---------------- */}
          {onglet === 'documents' && (
            <div className="devis-card clients-demande">
              <ClientDrive demandes={dossier.demandes} pieces={pieces} onChange={charger} generes={generes} />
            </div>
          )}

          {/* ---------------- Emails ----------------
              Tout ce qui est parti par email pour ce client (toutes ses
              demandes), tel que reçu : objet, corps, pièces jointes. */}
          {onglet === 'emails' && (
            <div className="devis-card clients-demande">
              <EmailsEnvoyes filtres={{ clientId: id }} />
            </div>
          )}
        </>
      )}

      {edition && dossier && (
        <ClientFormulaire
          client={dossier}
          onClose={() => setEdition(false)}
          onEnregistre={() => {
            setEdition(false);
            charger();
          }}
        />
      )}
    </div>
  );
}

export default ClientDossier;
